/**
 * Unified Questionnaire Logic for NC Routing & Interventions Matching
 * 
 * This module handles the branching questionnaire system that routes students
 * based on NC status and generates appropriate interventions and timetables.
 */

// NC Status routing question
export const NC_STATUS_QUESTION = {
  id: "nc_status",
  text: "Which best describes this pupil's current level of learning?",
  options: [
    { value: "at_nc", label: "At or near National Curriculum expectations" },
    { value: "nc_below12", label: "On National Curriculum but 1–2 years below" },
    { value: "below_nc", label: "Working below the National Curriculum (e.g. Engagement Model, pre-key stage)" },
    { value: "not_nc", label: "Not following National Curriculum (highly personalised)" }
  ]
};

// Plain Language Branch Questions (Below NC)
export const PLAIN_LANGUAGE_QUESTIONS = [
  // Expressive Language
  {
    id: "expressive_level",
    text: "How does the child communicate their needs and wants?",
    options: [
      { value: "full-sentences", label: "Uses full sentences and complex language" },
      { value: "short-phrases", label: "Uses short phrases and simple sentences" },
      { value: "single-words", label: "Uses single words and gestures" },
      { value: "non-verbal-no-initiation", label: "Non-verbal or minimal initiation, relies on adult interpretation" }
    ]
  },
  // Receptive Language
  {
    id: "receptive_level",
    text: "How well does the child understand spoken language?",
    options: [
      { value: "multi-step", label: "Follows multi-step instructions consistently" },
      { value: "one-to-two-step", label: "Follows 1-2 step instructions with some support" },
      { value: "single-step", label: "Needs instructions broken down into single steps" },
      { value: "minimal-understanding", label: "Minimal understanding, needs visual/physical support" }
    ]
  },
  // Cognition
  {
    id: "cognition_level",
    text: "How does the child engage with learning tasks?",
    options: [
      { value: "emerging-abstract", label: "Can work with abstract concepts and symbols" },
      { value: "needs-modelling", label: "Needs modelling and concrete examples" },
      { value: "concrete-only", label: "Works best with concrete, hands-on activities" },
      { value: "cause-effect", label: "Engages with cause and effect activities" }
    ]
  },
  // Regulation Independence
  {
    id: "regulation_independence",
    text: "How does the child manage their emotions and behavior?",
    options: [
      { value: "self-regulates", label: "Usually self-regulates with minimal support" },
      { value: "co-regulation", label: "Needs adult co-regulation at times" },
      { value: "frequent-support", label: "Frequently needs adult support to regulate" },
      { value: "constant-support", label: "Needs constant adult support for regulation" }
    ]
  },
  // Transitions Tolerance
  {
    id: "transitions_tolerance",
    text: "How does the child handle transitions and changes?",
    options: [
      { value: "manages-well", label: "Transitions well with minimal support" },
      { value: "needs-preparation", label: "Needs preparation and visual support" },
      { value: "struggles", label: "Struggles with transitions, needs structured support" },
      { value: "distressed", label: "Becomes distressed by transitions" }
    ]
  },
  // Group Tolerance
  {
    id: "group_tolerance",
    text: "How does the child respond to group situations?",
    options: [
      { value: "participates-confidently", label: "Participates confidently in groups" },
      { value: "participates-with-support", label: "Participates with some adult support" },
      { value: "reluctant", label: "Reluctant to join groups, prefers smaller settings" },
      { value: "avoids", label: "Avoids or becomes overwhelmed in group situations" }
    ]
  }
];

// NC Branch Questions (At/Near NC or 1-2 years below)
export const NC_BRANCH_QUESTIONS = {
  communication: [
    {
      id: "comm_communication",
      text: "How does the child communicate in class?",
      options: [
        { value: "fluent-age-expected", label: "Speaks fluently at age-expected level" },
        { value: "clear-needs-support", label: "Speaks clearly but may need support to organise ideas or use full sentences" },
        { value: "short-sentences", label: "Speaks in short sentences and sometimes struggles to make themselves understood" },
        { value: "simple-words", label: "Uses simple words or short phrases; sometimes relies on pointing/gesture" }
      ]
    },
    {
      id: "comm_understanding",
      text: "How well do they understand instructions?",
      options: [
        { value: "multi-step-consistent", label: "Follows multi-step instructions consistently" },
        { value: "one-two-step-support", label: "Follows 1–2 step instructions with some support" },
        { value: "single-step-visual", label: "Needs instructions broken down into single steps or supported with visuals" },
        { value: "frequent-repetition", label: "Needs frequent repetition and clarification" }
      ]
    },
    {
      id: "comm_social",
      text: "How do they interact socially?",
      options: [
        { value: "confident-participation", label: "Participates confidently in social and group activities" },
        { value: "needs-reminders", label: "Participates but may need reminders for turn-taking or personal space" },
        { value: "reluctant-groups", label: "Reluctant to join group activities, prefers parallel or adult interaction" },
        { value: "avoids-distressed", label: "Avoids or becomes distressed in group situations" }
      ]
    }
  ],
  cognition: [
    {
      id: "cog_learning",
      text: "How do they manage learning tasks compared to their peers?",
      options: [
        { value: "keeps-pace", label: "Keeps up with expected pace" },
        { value: "slightly-behind", label: "Slightly behind but engages with support" },
        { value: "needs-scaffolding", label: "Needs regular adult scaffolding to access tasks" },
        { value: "struggles-despite-support", label: "Struggles to access learning despite support" }
      ]
    },
    {
      id: "cog_understanding",
      text: "How do they demonstrate understanding?",
      options: [
        { value: "explains-clearly", label: "Explains or writes clearly in line with expectations" },
        { value: "explains-prompted", label: "Can explain when prompted or scaffolded" },
        { value: "short-responses", label: "Uses short verbal responses or relies on visuals" },
        { value: "struggles-show", label: "Often struggles to show what they know without significant support" }
      ]
    },
    {
      id: "cog_concentration",
      text: "How is their concentration and engagement during learning?",
      options: [
        { value: "maintains-focus", label: "Maintains focus with minimal prompts" },
        { value: "needs-reminders", label: "Needs regular reminders and check-ins" },
        { value: "structured-breaks", label: "Requires structured breaks and visual supports to maintain attention" },
        { value: "disengages", label: "Often disengages or needs 1:1 to access learning" }
      ]
    }
  ],
  semh: [
    {
      id: "semh_emotional",
      text: "How do they usually regulate their emotions in class?",
      options: [
        { value: "manages-well", label: "Manages emotions well with age-appropriate strategies" },
        { value: "needs-support-times", label: "Needs adult support at times but generally self-regulates" },
        { value: "regular-co-regulation", label: "Regularly needs co-regulation or adult intervention" },
        { value: "escalates-quickly", label: "Escalates quickly or withdraws, needs structured regulation support" }
      ]
    },
    {
      id: "semh_transitions",
      text: "How do they respond to transitions or unexpected changes?",
      options: [
        { value: "transitions-independently", label: "Transitions well independently" },
        { value: "manages-reminders", label: "Manages with reminders or visual support" },
        { value: "requires-structured", label: "Requires structured adult support for most transitions" },
        { value: "significant-distress", label: "Experiences significant distress at transitions" }
      ]
    },
    {
      id: "semh_groups",
      text: "How do they respond to group situations or busy environments?",
      options: [
        { value: "participates-confidently", label: "Participates confidently" },
        { value: "participates-support", label: "Participates with some support" },
        { value: "avoids-overwhelmed", label: "Avoids or is overwhelmed in group situations" },
        { value: "escalates-disengages", label: "Escalates or disengages in busy settings" }
      ]
    }
  ],
  sensory: [
    {
      id: "sensory_responses",
      text: "How do they respond to sensory input (noise, light, touch, etc.)?",
      options: [
        { value: "typical-responses", label: "Typical responses" },
        { value: "mild-sensitivities", label: "Mild sensitivities but manageable" },
        { value: "regular-seeking-avoidance", label: "Regular sensory seeking or avoidance behaviours" },
        { value: "strong-responses", label: "Strong sensory responses affecting participation" }
      ]
    },
    {
      id: "sensory_fine_motor",
      text: "Fine motor skills (writing, cutting, dressing)?",
      options: [
        { value: "age-expected", label: "Age expected" },
        { value: "mild-difficulties", label: "Mild difficulties needing light support" },
        { value: "noticeable-difficulty", label: "Noticeable difficulty, impacting independence" },
        { value: "significant-challenges", label: "Significant fine motor challenges requiring daily intervention" }
      ]
    },
    {
      id: "sensory_gross_motor",
      text: "Gross motor skills (coordination, balance, playground skills)?",
      options: [
        { value: "age-expected", label: "Age expected" },
        { value: "mild-delays", label: "Mild delays but functional" },
        { value: "noticeable-delays", label: "Noticeable delays, impacts PE or play" },
        { value: "significant-challenges", label: "Significant gross motor challenges" }
      ]
    }
  ]
};

/**
 * Route student to appropriate path based on NC status
 * @param {string} ncStatus - The NC status answer
 * @returns {string} - "INTERVENTIONS_ONLY" or "TIMETABLE_AND_INTERVENTIONS"
 */
export function routeToPath(ncStatus) {
  if (ncStatus === "at_nc" || ncStatus === "nc_below12") {
    return "INTERVENTIONS_ONLY";
  }
  return "TIMETABLE_AND_INTERVENTIONS";
}

/**
 * Route Primary student to specific path based on NC status and years below
 * @param {Object} profile - Student profile with phase and nc_status
 * @returns {string} - "PRIMARY_BELOW_NC" or "PRIMARY_INTERVENTIONS_ONLY"
 */
export function routePrimaryPlan(profile) {
  if (profile.phase !== 'primary') {
    throw new Error('Not primary phase');
  }
  
  // Determine years below based on NC status
  const yearsBelow = getYearsBelowFromNCStatus(profile.nc_status);
  
  if (profile.nc_status === 'below_nc' && yearsBelow >= 3) {
    return 'PRIMARY_BELOW_NC';
  }
  
  return 'PRIMARY_INTERVENTIONS_ONLY';
}

/**
 * Get years below NC from NC status
 * @param {string} ncStatus - The NC status answer
 * @returns {number} - Years below NC (0 for at/near NC, 1-2 for nc_below12, 3+ for below_nc)
 */
function getYearsBelowFromNCStatus(ncStatus) {
  switch (ncStatus) {
    case 'at_nc':
      return 0;
    case 'nc_below12':
      return 1; // 1-2 years below
    case 'below_nc':
      return 3; // 3+ years below
    case 'not_nc':
      return 4; // Not following NC at all
    default:
      return 0;
  }
}

/**
 * Determine which question branch to use based on NC status
 * @param {string} ncStatus - The NC status answer
 * @returns {string} - "PLAIN_LANGUAGE" or "NC_BRANCH"
 */
export function determineBranch(ncStatus) {
  if (ncStatus === "below_nc" || ncStatus === "not_nc") {
    return "PLAIN_LANGUAGE";
  }
  return "NC_BRANCH";
}

/**
 * Derive ability tiers and booleans from plain language answers
 * @param {Object} answers - The questionnaire answers
 * @returns {Object} - Ability profile with derived booleans and tiers
 */
export function deriveAbilityTiers(answers) {
  const profile = {
    expressive_level: answers.expressive_level,
    receptive_level: answers.receptive_level,
    cognition_level: answers.cognition_level,
    regulation_independence: answers.regulation_independence,
    transitions_tolerance: answers.transitions_tolerance,
    group_tolerance: answers.group_tolerance
  };

  // Map levels to tiers (A-E scale for Primary Below-NC)
  profile.expressiveTier = mapLevelToTier(profile.expressive_level, 'expressive');
  profile.receptiveTier = mapLevelToTier(profile.receptive_level, 'receptive');
  profile.cognitionTier = mapLevelToTier(profile.cognition_level, 'cognition');

  // Derive booleans based on ability levels
  profile.canUseSymbolicLanguage = 
    profile.expressive_level === "full-sentences" || 
    profile.expressive_level === "short-phrases";

  profile.canProcessSimpleReflection = 
    profile.cognition_level === "emerging-abstract" || 
    profile.cognition_level === "needs-modelling";

  profile.canUnderstandTokens = 
    profile.cognition_level === "emerging-abstract" && 
    profile.receptive_level !== "minimal-understanding";

  profile.canTolerateGroups = 
    profile.group_tolerance === "participates-confidently" || 
    profile.group_tolerance === "participates-with-support";

  return profile;
}

/**
 * Map questionnaire levels to A-E tiers for Primary Below-NC
 * @param {string} level - The questionnaire level
 * @param {string} type - The type of ability (expressive, receptive, cognition)
 * @returns {string} - Tier A, B, C, D, or E
 */
function mapLevelToTier(level, type) {
  if (!level) return 'E'; // Default to lowest tier if no answer

  const tierMappings = {
    expressive: {
      'full-sentences': 'A',
      'short-phrases': 'B', 
      'single-words': 'C',
      'non-verbal-no-initiation': 'E'
    },
    receptive: {
      'multi-step': 'A',
      'one-to-two-step': 'B',
      'single-step': 'D',
      'minimal-understanding': 'E'
    },
    cognition: {
      'emerging-abstract': 'A',
      'needs-modelling': 'B',
      'concrete-only': 'C',
      'cause-effect': 'D'
    }
  };

  return tierMappings[type]?.[level] || 'E';
}

/**
 * Map questionnaire answers to intervention focus tags
 * @param {Object} answers - The questionnaire answers
 * @param {string} branch - The question branch used
 * @returns {Object} - { focusTags: string[], needAreas: string[] }
 */
export function mapAnswersToInterventionTags(answers, branch) {
  const focusTags = [];
  const needAreas = [];

  if (branch === "PLAIN_LANGUAGE") {
    // Map plain language answers to tags
    if (answers.expressive_level === "non-verbal-no-initiation" || 
        answers.expressive_level === "single-words") {
      focusTags.push("communication", "pre-verbal", "intensive-interaction");
      needAreas.push("Communication & Interaction");
    } else if (answers.expressive_level === "short-phrases") {
      focusTags.push("communication", "language", "vocabulary");
      needAreas.push("Communication & Interaction");
    }

    if (answers.receptive_level === "minimal-understanding" || 
        answers.receptive_level === "single-step") {
      focusTags.push("attention", "engagement", "visual-supports");
      needAreas.push("Communication & Interaction");
    }

    if (answers.cognition_level === "cause-effect") {
      focusTags.push("cause-effect", "early-cognition", "agency");
      needAreas.push("Cognition & Learning");
    } else if (answers.cognition_level === "concrete-only") {
      focusTags.push("concrete-learning", "hands-on", "visual");
      needAreas.push("Cognition & Learning");
    }

    if (answers.regulation_independence === "constant-support" || 
        answers.regulation_independence === "frequent-support") {
      focusTags.push("regulation", "co-regulation", "SEMH");
      needAreas.push("Social, Emotional & Mental Health");
    }

    if (answers.transitions_tolerance === "distressed" || 
        answers.transitions_tolerance === "struggles") {
      focusTags.push("transitions", "routines", "visual-schedules");
      needAreas.push("Social, Emotional & Mental Health");
    }

    if (answers.group_tolerance === "avoids" || 
        answers.group_tolerance === "reluctant") {
      focusTags.push("1:1", "small-groups", "social-skills");
      needAreas.push("Social, Emotional & Mental Health");
    }

  } else if (branch === "NC_BRANCH") {
    // Map NC branch answers to tags
    if (answers.comm_communication === "simple-words" || 
        answers.comm_communication === "short-sentences") {
      focusTags.push("speech-language", "communication", "vocabulary");
      needAreas.push("Communication & Interaction");
    }

    if (answers.comm_understanding === "frequent-repetition" || 
        answers.comm_understanding === "single-step-visual") {
      focusTags.push("visual-supports", "scaffolding", "attention");
      needAreas.push("Communication & Interaction");
    }

    if (answers.comm_social === "avoids-distressed" || 
        answers.comm_social === "reluctant-groups") {
      focusTags.push("social-skills", "1:1", "small-groups");
      needAreas.push("Communication & Interaction");
    }

    if (answers.cog_learning === "struggles-despite-support" || 
        answers.cog_learning === "needs-scaffolding") {
      focusTags.push("literacy", "maths", "precision-teaching");
      needAreas.push("Cognition & Learning");
    }

    if (answers.cog_concentration === "disengages" || 
        answers.cog_concentration === "structured-breaks") {
      focusTags.push("attention", "engagement", "movement-breaks");
      needAreas.push("Cognition & Learning");
    }

    if (answers.semh_emotional === "escalates-quickly" || 
        answers.semh_emotional === "regular-co-regulation") {
      focusTags.push("regulation", "zones", "emotion-coaching");
      needAreas.push("Social, Emotional & Mental Health");
    }

    if (answers.semh_transitions === "significant-distress" || 
        answers.semh_transitions === "requires-structured") {
      focusTags.push("transitions", "routines", "social-stories");
      needAreas.push("Social, Emotional & Mental Health");
    }

    if (answers.semh_groups === "escalates-disengages" || 
        answers.semh_groups === "avoids-overwhelmed") {
      focusTags.push("small-groups", "regulation", "environment");
      needAreas.push("Social, Emotional & Mental Health");
    }

    if (answers.sensory_responses === "strong-responses" || 
        answers.sensory_responses === "regular-seeking-avoidance") {
      focusTags.push("sensory", "regulation", "sensory-circuits");
      needAreas.push("Sensory & Physical");
    }

    if (answers.sensory_fine_motor === "significant-challenges" || 
        answers.sensory_fine_motor === "noticeable-difficulty") {
      focusTags.push("fine-motor", "OT", "grip-strengthening");
      needAreas.push("Sensory & Physical");
    }

    if (answers.sensory_gross_motor === "significant-challenges" || 
        answers.sensory_gross_motor === "noticeable-delays") {
      focusTags.push("gross-motor", "movement", "sensory-circuits");
      needAreas.push("Sensory & Physical");
    }
  }

  return { focusTags: [...new Set(focusTags)], needAreas: [...new Set(needAreas)] };
}

/**
 * Get all questions for a specific branch
 * @param {string} branch - The question branch
 * @returns {Array} - Array of questions
 */
export function getQuestionsForBranch(branch) {
  if (branch === "PLAIN_LANGUAGE") {
    return PLAIN_LANGUAGE_QUESTIONS;
  } else if (branch === "NC_BRANCH") {
    return [
      ...NC_BRANCH_QUESTIONS.communication,
      ...NC_BRANCH_QUESTIONS.cognition,
      ...NC_BRANCH_QUESTIONS.semh,
      ...NC_BRANCH_QUESTIONS.sensory
    ];
  }
  return [];
}

/**
 * Get total number of questions for a branch
 * @param {string} branch - The question branch
 * @returns {number} - Total number of questions
 */
export function getQuestionCountForBranch(branch) {
  return getQuestionsForBranch(branch).length;
}
